import { useState, useEffect, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { RefreshCw, Database, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFiscalStore } from "@/store/fiscal-store";
import { cn } from "@/lib/utils";

export function ProtectedRoute({ children, requireAdmin = false }: { children: ReactNode; requireAdmin?: boolean }) {
  const { session, isAdmin, loading } = useAuth();
  const [showRecovery, setShowRecovery] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let timer: any;
    if (loading) {
      // Aumentado para 10s para dar tempo ao syncLocalDB em bases muito grandes
      timer = setTimeout(() => setShowRecovery(true), 10000);
    } else {
      setShowRecovery(false);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  const handleReset = async () => {
    // Preserva cookies e localStorage importante (como sessões do Supabase)
    // Limpa apenas o banco de dados local específico para forçar ressincronização
    try {
      // Deleta apenas o banco principal da aplicação
      window.indexedDB.deleteDatabase("NFeLocalDB_v3");
      
      // Limpa chaves de cache que podem causar travamento, mas preserva o login e histórico
      const keysToPurge = ["sync-status", "last-sync-hash"]; 
      keysToPurge.forEach(k => localStorage.removeItem(k));
      
      console.log("[auth] Cache específico limpo. Mantendo sessões, perfis e cookies.");
    } catch (e) {
      console.error("Erro ao limpar banco de dados específico:", e);
    }
    
    // Recarrega para a home preservando o estado de login
    window.location.reload();
  };

  const { setUserId, userId: storeUserId } = useFiscalStore();

  useEffect(() => {
    if (session?.user?.id && session.user.id !== storeUserId) {
      const id = session.user.id;
      setUserId(id);
      
      // Fluxo de Desbloqueio Automático vinculado ao Login
      (async () => {
        const { restoreKeyForUser } = await import("@/lib/local/crypto");
        const { syncLocalDBToStore } = await import("@/lib/local/sync");
        
        const unlocked = await restoreKeyForUser(id);
        await syncLocalDBToStore();
        
        console.log(`[auth] Usuário ${id} validado. Banco Local ${unlocked ? 'Auto-Desbloqueado' : 'Aguardando Senha'}.`);
      })();
    }
  }, [session, storeUserId, setUserId]);

  const [delayedLoading, setDelayedLoading] = useState(false);

  useEffect(() => {
    let timer: any;
    if (loading) {
      timer = setTimeout(() => setDelayedLoading(true), 800);
    } else {
      setDelayedLoading(false);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  if (loading) {
    if (!delayedLoading) return null; // Evita "flash" de sincronização se o redirect for rápido
    
    return (
      <div className="min-h-screen grid place-items-center bg-background p-6 relative overflow-hidden">
        {/* Dynamic Background Elements */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0 opacity-20">
          <div className="absolute -top-[10%] -left-[10%] w-[60%] h-[60%] bg-primary/5 liquid-blob blur-[100px]" />
          <div className="absolute -bottom-[10%] -right-[10%] w-[50%] h-[50%] bg-primary/10 liquid-blob blur-[120px]" />
        </div>

        <div className="max-w-md w-full glass-card p-10 flex flex-col items-center text-center space-y-8 animate-in zoom-in duration-500 border-primary/20 relative z-10 shadow-2xl shadow-primary/5">
          <div className={cn(
            "size-20 rounded-[2rem] flex items-center justify-center shadow-xl transition-all duration-700",
            showRecovery ? "bg-red-500/10 text-red-500 shadow-red-500/10" : "bg-primary/10 text-primary shadow-primary/10"
          )}>
            {showRecovery ? <ShieldAlert className="size-10" /> : <Database className="size-10 animate-pulse" />}
          </div>

          <div className="space-y-4">
            <h2 className={cn(
              "text-2xl font-black tracking-tighter uppercase transition-colors duration-700",
              showRecovery ? "text-red-600" : "text-primary"
            )}>
              {showRecovery ? "Sessão Interrompida" : "Sincronizando Sessão"}
            </h2>
            <p className="text-[11px] text-muted-foreground font-bold uppercase tracking-wide leading-relaxed">
              {showRecovery 
                ? "Detectamos uma falha na sincronização dos seus dados locais. Isso pode ocorrer devido a sessões expiradas ou dados corrompidos no navegador."
                : "Aguardando resposta do protocolo de autenticação. Por favor, aguarde enquanto validamos suas credenciais seguras..."}
            </p>
          </div>
          
          <div className="w-full space-y-3">
            <Button 
              onClick={handleReset} 
              className={cn(
                "w-full h-14 rounded-2xl transition-all font-black text-xs uppercase tracking-widest shadow-2xl border-0",
                showRecovery ? "bg-red-600 text-white hover:bg-red-700 shadow-red-600/30" : "bg-primary text-white hover:bg-primary/90 shadow-primary/30"
              )}
            >
              <RefreshCw className={cn("size-4 mr-2", !showRecovery && "animate-spin")} /> 
              Limpar Cache e Recarregar
            </Button>
            
            {!showRecovery && (
              <p className="text-[9px] font-black uppercase tracking-[0.2em] text-muted-foreground opacity-40 animate-pulse">
                Iniciando protocolo de recuperação em 6s...
              </p>
            )}

            {showRecovery && (
              <button 
                onClick={() => window.location.href = "/login"} 
                className="w-full text-[10px] font-black uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors mt-2"
              >
                Voltar para a tela de Login
              </button>
            )}
          </div>

          <div className="pt-4 border-t border-foreground/5 w-full flex items-center justify-center gap-2">
            <div className={cn("size-2 rounded-full", showRecovery ? "bg-red-500 animate-pulse" : "bg-primary animate-ping")} />
            <span className={cn("text-[9px] font-black uppercase", showRecovery ? "text-red-500" : "text-primary")}>
              {showRecovery ? "Recuperação Manual Necessária" : "Criptografando Túnel de Dados"}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/upload" replace />;
  }

  return <>{children}</>;
}
